export const name = "KLL_QUANTILES.MERGE_POINT_INT64";
export const summary = `Merges \`INT64\`-initialized KLL sketches into a new sketch, and then gets a specific quantile from the new sketch.`;
export const reference = `
## KLL_QUANTILES.MERGE_POINT_INT64

Merges \`INT64\`-initialized KLL sketches into a new sketch, and then gets a specific quantile from the new sketch.

\`\`\`
KLL_QUANTILES.MERGE_POINT_INT64(sketch, phi)
\`\`\`

### Description

Takes KLL sketches as \`BYTES\` and merges them, then extracts a single quantile from the merged sketch. The \`phi\` argument specifies the quantile to return as a fraction of the total number of rows in the input, normalized between 0 and 1. This means that the function will return a value _v_ such that approximately Φ \\* _n_ inputs are less than or equal to _v_, and a (1-Φ) \\* _n_inputs are greater than or equal to _v_. This is an aggregate function.

If the merged sketches were initialized with different precisions, the precision is downgraded to the lowest precision involved in the merge — except if the aggregations are small enough to still capture the input exactly — then the mergee's precision is maintained.

Returns an error if the underlying type of one or more input sketches isn't compatible with type \`INT64\`.

Returns an error if the input isn't a valid KLL quantiles sketch.


### Supported Argument Types

- \`sketch\`: \`BYTES\` KLL sketch initialized on \`INT64\` data type
- \`phi\`: \`FLOAT64\` between 0 and 1


### Return Type

\`INT64\`


### Example

The following query initializes two KLL sketches from five rows of data each. Then it merges these two sketches and returns the value of the ninth decile or 90th percentile of the merged sketch.



\`\`\`
SELECT KLL_QUANTILES.MERGE_POINT_INT64(kll_sketch, .9) AS quantile
FROM (SELECT KLL_QUANTILES.INIT_INT64(x, 1000) AS kll_sketch
      FROM (SELECT 1 AS x UNION ALL
            SELECT 2 AS x UNION ALL
            SELECT 3 AS x UNION ALL
            SELECT 4 AS x UNION ALL
            SELECT 5)
      UNION ALL
      SELECT KLL_QUANTILES.INIT_INT64(x, 1000) AS kll_sketch
      FROM (SELECT 6 AS x UNION ALL
            SELECT 7 AS x UNION ALL
            SELECT 8 AS x UNION ALL
            SELECT 9 AS x UNION ALL
            SELECT 10 AS x));

/*----------*
 | quantile |
 +----------+
 | 9 |
 *----------*/
\`\`\`


source: https://cloud.google.com/bigquery/docs/reference/standard-sql/kll_functions
`;
